import React,
{
	ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState
} from "react";
import {
	MaterialReactTable,
	MaterialReactTableProps,
	MRT_Cell,
	MRT_ColumnDef,
	MRT_ColumnFiltersState,
	MRT_FullScreenToggleButton,
	type MRT_RowSelectionState,
	MRT_ShowHideColumnsButton,
	MRT_TableInstance,
	MRT_ToggleFiltersButton,
	MRT_VisibilityState,
} from "material-react-table";
import {
	Box,
	Button, Dialog, DialogContent, DialogTitle, Grid,
	LinearProgress,
	ListItemIcon,
	MenuItem,
	ToggleButton,
	ToggleButtonGroup,
	Toolbar,
	Tooltip,
	Typography
} from "@mui/material";
import FileUpLoadIcon from "@mui/icons-material/FileUpload";
import { columnsNames, energyProjectTypes, type Project, processingPathMap, scenarioNote, excelColumnMaxLength, forecastDetachedHouseProjectTypes, statusList, fundingList, UploadScenarioProject, transmissionProjectTypes } from "./TableData";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from '@mui/icons-material/Edit';
import { apiRequest } from "../../services/utils/HttpUtils";
import { useMutation, useQuery, useQueryClient, UseMutateFunction } from "@tanstack/react-query";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CreateNewAccountModal } from "./CreateNewProjectModal";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as Excel from "exceljs/dist/exceljs.min.js";
import { saveAs } from "file-saver";
import { createAssumptions } from "../../graphql/mutations_assumptions";
import { getAllLists, GetScenarioProjectByID, UpsertScenarioProject } from "../../graphql/queries_pipelineEditor";
import { pipelineEditorColumn } from "./componentsConfig/pipelineEditorConfig";
import { UpsertProjectRecord } from "../../services/pipelineEditorService/UpsertProjectRecord";
import { useMsal } from "@azure/msal-react";
import { caveatIntro } from "../../assets/texts/pageTextContent";
import dayjs from "dayjs";
import '../../css/site.css'; // Importing stylesheet
import { validateEndDate, validateStartDate } from "../../services/utils/validationUtils";
import { CUSTOM_ASSUMPTION_QUERY } from "../../graphql/queries_custom_assumptions";
import useBaseLineAssumption from "../../hooks/useBaseLineAssumption";
import { calculateProcessingPath, getForecast, getFundingMode, getLocations, getOngoing, getProjectLatestStatus, getProjectTypes, getSortedLocationByState, getSource, getStates } from "../../services/utils/excelUtils";
import { UPLOAD_SCENARIO } from "../../graphql/queries_scenarioEditor";
import {getUserInfo} from "../../graphql/queries_user_info";
const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)


type Props = {
	setTabIndex: React.Dispatch<React.SetStateAction<number>>,
	scenarioID: string,
	runModel: UseMutateFunction
};

export default function PipelineEditor(props: Props) {
	const {
		setTabIndex,
		scenarioID,
		runModel } = props;
	const client = useQueryClient();
  const { data: userInfo } = getUserInfo();
	const tableInstanceRef = useRef<MRT_TableInstance<Project>>(null);
	const rerender = useReducer(() => ({}), {})[1];
	const [showColumnFilters, setShowColumnFilters] = useState(false);
	const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
	const [showEdited, setShowEdited] = useState(0);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
		{
			ProjectID: false,
			PeriodStartYear: false,
			PeriodEndYear: false,
			km: false,
			MW: false,
			M2: false,
			ProcessingPath: false,
			Errors: false,
		},
	);
	const [validationErrors, setValidationErrors] = useState<{
		[cellId: string]: string;
	}>({});
	const [dateError, setDateError] = useState("")
	const [downloading, setDownloading] = useState(false);
	const { instance, accounts } = useMsal();
	const [scenarioTitle, setScenarioTitle] = useState("")
	const { baseLineAssumption } = useBaseLineAssumption({ scenarioID, instance, accounts });
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [previewFlag, setPreviewFlag] = useState(false)
	const [previewData, setPreviewData] = useState<UploadScenarioProject[]>([])
	const [uploadError, setUploadError] = useState(false)

	const clearPreview = () => {
		setPreviewFlag(false);
		setUploadError(false);
		setColumnVisibility({
			...columnVisibility,
			Errors: false,
		})
		setPreviewData([]);
	}

	const handleConfirmSubmit = () => {
		if (!uploadError && previewData.length > 0) {
			uploadScenarioProjects.mutate({
				inScenarioID: scenarioID,
				inProjectJSON: JSON.stringify(previewData)
			})
		}
		clearPreview();
	}

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = async (event) => {
				if (event.target && event.target.result) {
					const workbook = new Excel.Workbook();
					await workbook.xlsx.load(event.target.result as ArrayBuffer);
					const worksheet = workbook.worksheets[0];
					const jsonData: any[] = [];
					let headers: string[] = [];

					//validation should happen here
					worksheet.eachRow((row, rowNumber) => {
						const rowValues = row.values;
						const rowErrors: string[] = [];
						// Row 4 is the header row
						if (rowNumber === scenarioNote.length + 1) {
							headers = rowValues.slice(1); // Remove first empty cell
						} else if (rowNumber > scenarioNote.length + 1) {
							const rowObject: { [key: string]: any } = {};
							headers.forEach((header, index) => {
								let cellValue = rowValues[index + 1];
								const columnName = Object.keys(columnsNames).find(key => columnsNames[key] === header);

								if (columnName) {
									// change null or undefined to empty string
									if (!cellValue || cellValue === "null" || cellValue === "undefined" || cellValue == null) {
										cellValue = "";
									}


									//ProjectName must not be null
									if (columnName === "ProjectName" && cellValue === "") {
										rowErrors.push("ProjectName must not be null");
									}
									//State must be in the state list
									if (columnName === "State" && !getStates(AllLists).includes(cellValue)) {

										rowErrors.push("State must be in the state list");
									}
									//Investment must be a number
									if (columnName === "Investment" && isNaN(cellValue)) {
										rowErrors.push("Investment must be a number");
									}
									//ProjectType must be in the project type list
									if (columnName === "RawProjectType" && !getProjectTypes(AllLists).includes(cellValue)) {
										rowErrors.push("Project Type must be in the project type list");
									}

									//Construction Start Date need to be in the correct format
									if (columnName === "StartDateConstruction" && cellValue !== "") {
										// if typeof cellValue is string, then use cellvalue.tostring
										if (typeof cellValue === "object") {
											cellValue = dayjs(cellValue).format("DD/MM/YYYY") === "Invalid Date" ? cellValue : dayjs(cellValue).format("DD/MM/YYYY")
										} else {
											cellValue = cellValue.toString()
										}


									}
									//Construction End Date need to be in the correct format
									if (columnName === "EndDateConstruction" && cellValue !== "") {
										if (typeof cellValue === "object") {
											cellValue = dayjs(cellValue).format("DD/MM/YYYY") === "Invalid Date" ? cellValue : dayjs(cellValue).format("DD/MM/YYYY")
										} else {
											cellValue = cellValue.toString()
										}



									}

									if (columnName === "PeriodStartYear" && cellValue !== "") {
										if (typeof cellValue === "object") {
											cellValue = dayjs(cellValue).format("DD/MM/YYYY") === "Invalid Date" ? cellValue : dayjs(cellValue).format("DD/MM/YYYY")
										} else {
											cellValue = cellValue.toString()
										}
									}
									if (columnName === "PeriodEndYear" && cellValue !== "") {
										if (typeof cellValue === "object") {
											cellValue = dayjs(cellValue).format("DD/MM/YYYY") === "Invalid Date" ? cellValue : dayjs(cellValue).format("DD/MM/YYYY")
										} else {
											cellValue = cellValue.toString()
										}
									}

									//Fund Mode must be in the funding list
									if (columnName === "FundingMode" && !getFundingMode(fundingList).includes(cellValue)) {
										rowErrors.push("Funding Mode must be in the funding list");
									}

									//The flags are handled in the backend

									if (columnName === "OperateStatus") {
										rowObject["RemoveProjectFromModel"] = 0;
										rowObject["EditedProject"] = 0;

									}


									rowObject[columnName] = cellValue;

								}
							})

							// Construction start date cannot be null for anything other than forecast projects
							// if (rowObject["StartDateConstruction"] === "" && rowObject["Forecast"] !== "yes") {
							// 	rowErrors.push("Construction Start Date cannot be null for anything other than forecast projects");
							// }
							// Construction start date cannot be after the end date
							if (dayjs(rowObject["StartDateConstruction"], "DD/MM/YYYY").isAfter(dayjs(rowObject["EndDateConstruction"], "DD/MM/YYYY"))) {
								rowErrors.push("Construction Start Date cannot be after the end date");
							}
							// Construction end date cannot be null for anything other than forecast projects
							// if (rowObject["EndDateConstruction"] === "" && rowObject["Forecast"] !== "yes") {
							// 	rowErrors.push("Construction End Date cannot be null for anything other than forecast projects");
							// }
							// Construction end date cannot be before the start date
							if (dayjs(rowObject["EndDateConstruction"], "DD/MM/YYYY").isBefore(dayjs(rowObject["StartDateConstruction"], "DD/MM/YYYY"))) {
								rowErrors.push("Construction End Date cannot be before the start date");
							}
							//Period Start Year is required for forecast projects
							if (rowObject["PeriodStartYear"] === "" && rowObject["Forecast"] === "yes") {
								rowErrors.push("Period Start Year is required for forecast projects");
							}
							//Period End Year is required for forecast projects
							if (rowObject["PeriodEndYear"] === "" && rowObject["Forecast"] === "yes") {
								rowErrors.push("Period End Year is required for forecast projects");
							}
							//KM must be a number
							//can be null for non-transmission projects
							if (rowObject["km"] !== "" && isNaN(rowObject["km"])) {
								rowErrors.push("KM must be a number");
							}
							//KM can not be null for transmission projects apart from UTS_Transmission
							if (rowObject["km"] === "" && [transmissionProjectTypes[1], transmissionProjectTypes[2]].includes(rowObject["RawProjectType"]) && rowObject["Source"] !== "UTS_Transmission") {
								rowErrors.push("KM can not be null for transmission projects")
							}

							//MW must be a number
							//can be null for non-energy projects
							if (rowObject["MW"] !== "" && isNaN(rowObject["MW"])) {
								rowErrors.push("MW must be a number");
							}
							//MW can not be null for energy projects
							if (rowObject["MW"] === "" && energyProjectTypes.includes(rowObject["RawProjectType"])) {
								rowErrors.push("MW can not be null for energy projects")
							}

							//M2 must be a number
							//can be null for non-detached house projects
							if (rowObject["M2"] !== "" && isNaN(rowObject["M2"])) {
								rowErrors.push("M2 must be a number");
							}
							//M2 can not be null for detached house projects
							if (rowObject["M2"] === "" && forecastDetachedHouseProjectTypes.includes(rowObject["RawProjectType"])) {
								rowErrors.push("M2 can not be null for detached house projects")
							}



							// calculate processing path
							const processingPath = calculateProcessingPath(rowObject["Forecast"], rowObject["Ongoing"], rowObject as UploadScenarioProject);
							rowObject["ProcessingPath"] = processingPath;
							// add error column
							rowObject["Errors"] = rowErrors.join(", ");
							// change LocationID to real LocationID
							// @ts-ignore
							const location = AllLists?.executeGetLocationList.find((item) => item.LocationName == rowObject["LocationID"]);
							if (location) {
								rowObject["LocationID"] = location.LocationID;
							}
							// construction start date should not be null for only processing path is 1, 2,7
							if (rowObject["StartDateConstruction"] === "" && [1, 2, 7].includes(processingPath)) {
								rowErrors.push("Construction Start Date cannot be null");
							}
							// construction end date should not be null for only processing path is 1, 2,7
							if (rowObject["EndDateConstruction"] === "" && [1, 2, 7].includes(processingPath)) {
								rowErrors.push("Construction End Date cannot be null");
							}

							jsonData.push(rowObject);
						}
					});

					// sort data based on errors
					jsonData.sort((a, b) => {
						if (a["Errors"] === "" && b["Errors"] !== "") {
							return 1;
						}
						if (a["Errors"] !== "" && b["Errors"] === "") {
							return -1;
						}
						return 0;
					});

					//set up preview flag, data
					setPreviewFlag(true);
					setPreviewData(jsonData);
					setColumnVisibility({
						...columnVisibility,
						Errors: true,
					})
					//set up upload error
					setUploadError(jsonData.filter((item) => item["Errors"] !== "").length > 0);
				}
			}

			reader.readAsArrayBuffer(file);
		} else {
			console.log('No file selected');
		}

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}


	useEffect(() => {
		// @ts-ignore
		client.getQueryData(["scenarioList"])?.executeGetScenarioList.map((item) => {
			if (item.ScenarioID == scenarioID) {
				setScenarioTitle(item.ScenarioTitle)
			}
		});
	}, [client.getQueryData(["scenarioList"])])

	// Download excel file
	useEffect(() => {
		const states = getStates(AllLists)
		const projectTypes = getProjectTypes(AllLists)
		const source = getSource(AllLists)
		const locations = getLocations(AllLists)
		const projectLatestStatusList = getProjectLatestStatus(statusList)
		const funding = getFundingMode(fundingList)

		function addDataToWorksheet(worksheet, data, startRow, validation) {
			// helper function definitions
			function formatHeader(headerList) {
				return headerList.map((item) => columnsNames[item])
			}
			const addHeaderRow = (data, worksheet) => {
				const headerRow = formatHeader(Object.keys(data[0]));
				const row = worksheet.addRow(headerRow);

				// Highlight specific columns
				row.eachCell((cell, colNumber) => {
					const colName = Object.keys(data[0])[colNumber - 1];
					if (["Errors"].includes(colName)) {
						cell.fill = {
							type: 'pattern',
							pattern: 'solid',
							fgColor: { argb: 'FFFF0000' } // Red color
						};
					}
				});
			};

			const processCell = (cell, colNumber, header) => {
				// Helper variables and functions
				const getColumnLetter = (col) => String.fromCharCode(64 + col);
				const cellRange = `${getColumnLetter(colNumber)}1:${getColumnLetter(colNumber)}${excelColumnMaxLength}`;
				const applyDataValidation = (range, formulae) => {
					worksheet.dataValidations.add(range, {
						type: 'list',
						allowBlank: false,
						formulae: formulae,
					});
				};

				if (header === columnsNames["MW"] || header === columnsNames["km"] || header === columnsNames["M2"]) {
					cell.value = Number(cell.value);
				} else if (header === columnsNames["StartDateConstruction"] || header === columnsNames["EndDateConstruction"] || header === columnsNames["PeriodStartYear"] || header === columnsNames["PeriodEndYear"]) {
					cell.numFmt = "dd/mm/yyyy";
				}
				else if (header === columnsNames["Investment"]) {
					cell.value = Number(cell.value);
					cell.numFmt = '"$"#,##0.00';
				} else {
					cell.value = String(cell.value);
				}

				if (header === columnsNames["RawProjectType"]) {
					applyDataValidation(cellRange, [`'Project Types'!$A$1:$A$${projectTypes.length}`]);
				}

				if (header === columnsNames["LocationID"]) {
					const stateValue = cell._row._cells[3]?.value; // state column
					const columnIndex = states.indexOf(stateValue.toUpperCase());
					if (columnIndex !== -1) {
						// INDIRECT will consider the note row and header row
						applyDataValidation(cellRange, [`INDIRECT(D1)`])
					}
				}

				if (header === columnsNames["State"]) {
					applyDataValidation(cellRange, [`'States'!$A$1:$A$${states.length}`]);
				};

				if (header === columnsNames["Source"]) {
					applyDataValidation(cellRange, [`'Source'!$A$1:$A$${source.length}`]);
				}

				if (header === columnsNames["ProjectStage"]) {
					applyDataValidation(cellRange, [`'Status'!$A$1:$A$${projectLatestStatusList.length}`]);
				}

				if (header === columnsNames["FundingMode"]) {
					applyDataValidation(cellRange, [`'Funding'!$A$1:$A$${funding.length}`]);
				}


				// change null to empty string
				if (cell.value === null || cell.value === undefined || cell.value === "null") {
					cell.value = "";
				}
			};

			const processRow = (row, startRow) => {
				const newRow = worksheet.addRow(Object.values(row));
				newRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
					const header = worksheet.getRow(startRow).getCell(colNumber).value;
					processCell(cell, colNumber, header);
				});
			};

			// validation
			if (validation || worksheet.name === "Assumptions") {
				addHeaderRow(data, worksheet);
				data.forEach((row) => processRow(row, startRow));
			} else {
				if (worksheet.name === "Locations") {
					worksheet.addRow(states);
					data.forEach((location, rowIndex) => {
						const columnIndex = states.indexOf(location.state.toUpperCase()) + 1;
						for (let i = 2; i <= rowIndex + 2; i++) {
							let found = false;
							const row = worksheet.getRow(i);
							if (!row.getCell(columnIndex).value && !found) {
								row.getCell(columnIndex).value = location.locationName;
								found = true;
								break;
							}
						}
					});
				} else {
					data.forEach((item) => worksheet.addRow([item]));
				}
			}
		}

		// @ts-ignore
		if (downloading && scenarioAssumption?.executeGetScenarioCustomAssumption) {
			// @ts-ignore
			const project_data = (projectList?.executeGetScenarioProjectList).map((ListItem) => {
				// @ts-ignore
				const locationName = AllLists?.executeGetLocationList.find(item => item.LocationID === ListItem.LocationID)?.LocationName;
				const newItem = {
					...ListItem,
					LocationID: locationName,
					Forecast: getForecast(ListItem.ProcessingPath),
					Ongoing: getOngoing(ListItem.ProcessingPath),
					OperateStatus: ListItem.RemoveProjectFromModel === 1
						? "Deleted"
						: ListItem.EditedProject === 1
							? "Edited"
							: "Unchanged",
				};

				delete newItem.RemoveProjectFromModel;
				delete newItem.EditedProject;
				delete newItem.ReadonlyProject;
				delete newItem.ProcessingPath;
				delete newItem.ScenarioID;
				delete newItem.ScenarioProjID;

				return newItem;
			}) ?? [];

			const preview_project_data = previewData.map((ListItem) => {
				// @ts-ignore
				const locationName = AllLists?.executeGetLocationList.find(item => item.LocationID === ListItem.LocationID)?.LocationName;
				const newItem = {
					...ListItem,
					LocationID: locationName,
					Forecast: getForecast(ListItem.ProcessingPath),
					Ongoing: getOngoing(ListItem.ProcessingPath),
					OperateStatus: ListItem.RemoveProjectFromModel === 1
						? "Deleted"
						: ListItem.EditedProject === 1
							? "Edited"
							: "Unchanged",
				};

				// move errors of the new item to the end
				const errors = newItem.Errors;
				delete newItem.Errors;
				newItem.Errors = errors;

				delete newItem.RemoveProjectFromModel;
				delete newItem.EditedProject;
				delete newItem.ReadonlyProject;
				delete newItem.ProcessingPath;


				return newItem;
			}) ?? [];

			// @ts-ignore
			const assumptions_data = scenarioAssumption?.executeGetScenarioCustomAssumption.map((item) => {
				//@ts-ignore
				const projectType = AllLists?.executeGetRawProjectTypes.find((type) => type.RawProjectTypeID == item.ProjectTypeID)?.RawProjectType
				return {
					scenarioID: scenarioID,
					ProjectTypeID: projectType,
					TimePlanning: item.TimePlanning,
					TimeConstruction: item.TimeConstruction,
					TimeCommissioning: item.TimeCommissioning,
					InvestmentPlanning: item.InvestmentPlanning,
					InvestmentConstruction: item.InvestmentConstruction,
					InvestmentCommissioning: item.InvestmentCommissioning,
				}
			})
			// @ts-ignore
			const tempBaseLineAssumption = baseLineAssumption?.executeGetScenarioAssumption.map((assumption: any) => {
				return {
					scenarioID: scenarioID,
					ProjectTypeID: "Baseline",
					TimePlanning: assumption.Planning,
					TimeConstruction: assumption.Construction,
					TimeCommissioning: assumption.Commissioning,
					InvestmentPlanning: assumption.PlanningCostPercentage,
					InvestmentConstruction: assumption.ConstructionCostPercentage,
					InvestmentCommissioning: assumption.CommissioningCostPercentage,
				}
			})
			const new_assumption_data = [
				{ ...tempBaseLineAssumption[0] },
				...assumptions_data
			]

			const workbook = new Excel.Workbook();
			workbook["creator"] = "Infrastructure Australia";
			const worksheetProjectData = workbook.addWorksheet("Project data");
			const worksheetAssumptions = workbook.addWorksheet("Assumptions");
			const worksheetProjectTypes = workbook.addWorksheet("Project Types");
			const worksheetLocations = workbook.addWorksheet("Locations");
			const worksheetStates = workbook.addWorksheet("States");
			const worksheetSource = workbook.addWorksheet("Source");
			const worksheetStatus = workbook.addWorksheet("Status");
			const worksheetFunding = workbook.addWorksheet("Funding");


			scenarioNote.forEach((line, index) => {
				const noteRow = worksheetProjectData.addRow([line]);
				noteRow.font = { bold: true };
			});

			addDataToWorksheet(worksheetProjectData, previewFlag ? preview_project_data : project_data, scenarioNote.length + 1, true);
			addDataToWorksheet(worksheetAssumptions, new_assumption_data, 1, false);
			addDataToWorksheet(worksheetProjectTypes, projectTypes, 1, false);
			addDataToWorksheet(worksheetLocations, locations, 1, false);
			addDataToWorksheet(worksheetStates, states, 1, false);
			addDataToWorksheet(worksheetSource, source, 1, false);
			addDataToWorksheet(worksheetStatus, projectLatestStatusList, 1, false);
			addDataToWorksheet(worksheetFunding, funding, 1, false);

			const sortedLocationsByState = getSortedLocationByState(locations, states);

			// Create named ranges for each state in 'locations' worksheet
			Object.keys(sortedLocationsByState).forEach((state, colIndex) => {
				const colLetter = String.fromCharCode(65 + colIndex); // Convert column index to letter
				const range = `${colLetter}2:${colLetter}${sortedLocationsByState[state].length + 1}`;
				workbook.definedNames.add(`Locations!${range}`, state);
			});

			workbook.xlsx.writeBuffer().then(function (buffer) {
				const blob = new Blob([buffer], { type: "application/xlsx" });
				saveAs(blob, scenarioTitle + ".xlsx");
			});
			setDownloading(false);
			setPreviewFlag(false);
			setUploadError(false);
		}
	},
		[downloading, previewFlag]);

	useEffect(() => {
		queryAssumption();
	}, []);

	const uploadScenarioProjects = useMutation(["uploadScenarioProject"], {
		mutationFn: (variables: any) => {
			return apiRequest(
				{
					query: UPLOAD_SCENARIO,
					variables: variables,
					instance: instance,
					accounts: accounts
				})
		},
		onSuccess: () => {
			client.invalidateQueries(["projectList"]);
			clearPreview();
		},
	});


	const { queryAssumption, scenarioAssumption } = createAssumptions(
		{
			query: CUSTOM_ASSUMPTION_QUERY,
			variables: {
				inScenarioID: scenarioID,
				inProjectTypeID: 0
			},
			instance: instance,
			accounts: accounts
		}
	);

	const { data: AllLists, isFetching } = useQuery(
		{
			queryKey: ["Lists"],
			queryFn: () => {
				return apiRequest(
					{
						query: getAllLists,
						variables: {
              // @ts-ignore
							inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction ?? "",
							inScenarioID: ""
						},
						instance: instance,
						accounts: accounts
					}
				)
			},
			refetchOnWindowFocus: false,
			cacheTime: Infinity,
      enabled: !!userInfo
		});

	const { data: projectList, isLoading } = useQuery(
		{
			queryKey: ["projectList", showEdited],
			queryFn: () => {
				return apiRequest(
					{
						query: GetScenarioProjectByID,
						variables: {
							inScenarioID: scenarioID,
							inEdited: showEdited
						},
						instance: instance,
						accounts: accounts
					})
			},
			refetchOnWindowFocus: false
		});
	//@ts-ignore
	const editableProj = !Object.keys(validationErrors).length > 0;
	const upsertScenario = useMutation(["upsertScenario"], {
		mutationFn: (variables: any) => {
			return apiRequest(
				{
					query: UpsertScenarioProject,
					variables: variables,
					instance: instance,
					accounts: accounts
				})
		},
		onSuccess: () => {
			client.invalidateQueries(["projectList"]);
		},
	});
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const handleSaveRow: MaterialReactTableProps<Project>["onEditingRowSave"] =
		async ({ exitEditingMode, row, values }) => {
			//send/receive api updates here
			if (!Object.keys(validationErrors).length) {
				if (validateDatetime(values)) {
					setDateError("");
					upsertScenario.mutate(
						UpsertProjectRecord(
							scenarioID,
							"editing",
							values,
							// @ts-ignore
							projectList?.executeGetScenarioProjectList[row.index])
					);

					// optimistic update
					values.StartDateConstruction = validateStartDate(values.StartDateConstruction, values.ProcessingPath, true);
					values.EndDateConstruction = validateEndDate(values.EndDateConstruction, values.ProcessingPath, true);
					row[row.index] = values;
					exitEditingMode(); //required to exit editing mode
				}
			}
		};
	const validateRequired = (value: string) => !value.length ? "is required" : "";
	const validateDatetime = (values) => {
		if (!values.EndDateConstruction || !values.StartDateConstruction) {
			setDateError("Invalid construction date range, Construction Dates can not be empty")
			return false
		}
		if (dayjs(values.EndDateConstruction).isBefore(dayjs(values.StartDateConstruction))) {
			setDateError("Invalid construction date range, Construction Start Date should be before Construction End Date.")
			return false
		}
		return true
	}
	const validateProjectName = (projectName, cell) => {
		// @ts-ignore
		if (projectList?.executeGetScenarioProjectList.filter((item) =>
			item['ProjectName'] == projectName).length > 0
			&& projectName != cell.getValue()) {
			return "Project name need to be unique"
		}
		return ""
	};
	const validateInvestment = (investment) => Number(investment) < 0 ? "Investment is invalid" : "";

	const validateProjectType = (projectType, cell) => {
		const tempProcessingPath = cell.row.getAllCells().find((item) => item.column.id === "ProcessingPath").getValue()
		const preProjectType = cell.getValue();

		const isErrorProjectType = !processingPathMap[tempProcessingPath](preProjectType, projectType)[0]
		const errorMessage = processingPathMap[tempProcessingPath](preProjectType, projectType)[1]
		if (isErrorProjectType) {
			return errorMessage
		}

		// TODO: add housing project types validation once m2 field is build
		return ""
	}

	const validateMegawatt = (megawatt, cell) => {
		const currentProjectType = cell.row.getAllCells().find((item) => item.column.id === "RawProjectType").getValue();
		if (energyProjectTypes.indexOf(currentProjectType) === -1 && megawatt != 0) {
			return "MW only exist in energy project"
		}
		// Check empty string
		if (megawatt == 0) {
			return "Please enter non empty MW"
		}
		return ""
	}

	const validateM2 = (m2, cell) => {
		const currentProjectType = cell.row.getAllCells().find((item) => item.column.id === "RawProjectType").getValue();
		if (forecastDetachedHouseProjectTypes.indexOf(currentProjectType) === -1 && m2 != 0) {
			return "M2 only exist in detached house project"
		}
		// Check empty string
		if (m2 == 0) {
			return "Please enter non empty M2"
		}
		return ""
	}

	const validateKm = (km, cell) => {

		if (km == 0) {
			return "Please enter non empty KM"
		}
		return ""

	}

	//  define validation rules for each cell
	const validateCellValue = (cell: MRT_Cell<Project>, value: string | number): string => {
		if (cell.column.id === 'ProjectName') {
			return validateProjectName(value.toString(), cell);
		} else if (cell.column.id === 'Investment') {
			return validateInvestment(+value);
		} else if (cell.column.id === "RawProjectType") {
			return validateProjectType(value, cell);
		} else if (cell.column.id === "MW") {
			return validateMegawatt(value, cell);
		} else if (cell.column.id === "M2") {
			return validateM2(value, cell);
		} else if (cell.column.id === "km") {
			return validateKm(value, cell);
		}
		else {
			return validateRequired(value.toString());
		}
	};
	const getCommonEditTextFieldProps = useCallback(
		(
			cell: MRT_Cell<Project>,
		): MRT_ColumnDef<Project>['muiTableBodyCellEditTextFieldProps'] => {
			return {
				error: !!validationErrors[cell.id],
				helperText: validationErrors[cell.id],
				onBlur: (event) => {
					const ErrorMessage = validateCellValue(cell, event.target.value);
					if (ErrorMessage) {
						//set validation error for cell if invalid
						setValidationErrors({
							...validationErrors,
							[cell.id]: `${ErrorMessage}`,
						});
					} else {
						//remove validation error for cell if valid
						delete validationErrors[cell.id];
						setValidationErrors({
							...validationErrors,
						});
					}
				},
			};
		},
		[validationErrors],
	);

	const handleCreateNewRowModal = (values: Project) => {
		//send/receive api updates here
		upsertScenario.mutate(
			UpsertProjectRecord(
				scenarioID,
				"creating",
				values)
		);
	};

	function handleUpdateTab(index: number) {
		setTabIndex(index);
	}

	const handleShowEditedChange = (event) => {
		if (event.target.value != null) {
			setShowEdited(Number(event.target.value));
		}
	};

	const columns = useMemo(() => pipelineEditorColumn(AllLists, getCommonEditTextFieldProps),
		[AllLists, getCommonEditTextFieldProps]);

	function handleDeleteProject(row) {
		upsertScenario.mutate(
			UpsertProjectRecord(
				scenarioID,
				"deleting",
				// @ts-ignore
				projectList?.executeGetScenarioProjectList[row.index])
		);
	}
	function handleRestoreProject(row) {
		upsertScenario.mutate(
			UpsertProjectRecord(
				scenarioID,
				"restoring",
				// @ts-ignore
				projectList?.executeGetScenarioProjectList[row.index])
		);
	}
	return (
		<Box sx={{ zIndex: "1000" }}>
			<Toolbar
				sx={() => ({
					borderRadius: "4px",
					display: "flex",
					flexDirection: {
						xs: "column",
						lg: "row",
					},
					gap: "1rem",
					justifyContent: "space-between",
					p: "1.5rem 0",
				})}
			>
				<Box>
					<Grid container spacing={2} alignItems="center">
						<Grid item>
							<Button
								onClick={() => setCreateModalOpen(true)}
								variant="outlined"
								sx={{
									borderRadius: "35px",
								}}
								disabled={!editableProj}
							>
								Add a project
								<AddIcon />
							</Button>
						</Grid>
						<Grid item>
							<Tooltip arrow title={"Filter Edited Item"}>
								<ToggleButtonGroup
									value={showEdited}
									exclusive
									onChange={handleShowEditedChange}
									aria-label="Edited Filter"
									size="small"
								>
									<ToggleButton value={1}>Edited</ToggleButton>
									<ToggleButton value={0}>All</ToggleButton>
								</ToggleButtonGroup>
							</Tooltip>
						</Grid>
						<Grid item>

						</Grid>
					</Grid>

				</Box>
				<Typography variant={"h5"}>
					Scenario name: {scenarioTitle}
				</Typography>
				<Box>
					<Button
						onClick={() => handleUpdateTab(2)}
						variant="contained"
					>

						Assumptions
						<ArrowForwardIcon />
					</Button>
				</Box>
			</Toolbar>
			<MaterialReactTable
				// @ts-ignore
				columns={columns}
				// @ts-ignore
				data={previewFlag ? previewData ?? [] : projectList?.executeGetScenarioProjectList ?? []}
				enableRowActions
				enableRowVirtualization
				rowVirtualizerProps={{ overscan: 10 }}
				enablePagination={false}
				// Issue with Material table conditional editing (v1 1.14), dummy condition need to be in here
				// so the editing row state is managed properly internally
				enableEditing={(row) => row.index % 1 === 0}
				onEditingRowCancel={() => { setValidationErrors({}) }}
				enableSorting={true}
				globalFilterFn="includesString"
				editingMode="row"
				enableFacetedValues
				enableClickToCopy
				enableColumnResizing
				enableTopToolbar
				positionGlobalFilter="left"
				onEditingRowSave={handleSaveRow}
				enableStickyHeader
				muiTableContainerProps={{ sx: { maxHeight: "40vh" } }}
				onShowColumnFiltersChange={(updater) => {
					setShowColumnFilters((prev) =>
						updater instanceof Function ? updater(prev) : updater,
					);
					setColumnFilters([]);
					queueMicrotask(rerender); //hack to rerender after state update
				}}
				onColumnFiltersChange={(updater) => {
					setColumnFilters((prev) =>
						updater instanceof Function ? updater(prev) : updater,
					);
					queueMicrotask(rerender); //hack to rerender after state update
				}}
				onColumnVisibilityChange={(updater) => {
					setColumnVisibility((prev) =>
						updater instanceof Function ? updater(prev) : updater,
					);
					queueMicrotask(rerender); //hack to rerender after state update
				}}
				onRowSelectionChange={(updater) => {
					setRowSelection((prev) =>
						updater instanceof Function ? updater(prev) : updater,
					);
					queueMicrotask(rerender); //hack to rerender after state update
				}}
				initialState={{
					showGlobalFilter: true,
					density: "compact"
				}}
				state={{
					columnVisibility,
					rowSelection,
					columnFilters,
					showColumnFilters,
					isLoading,
					showAlertBanner: !!dateError || !!previewFlag,
					showProgressBars: isFetching,
					columnPinning: {
						right: [],
						left: ['mrt-row-actions']
					}
				}}
				tableInstanceRef={tableInstanceRef}
				displayColumnDefOptions={{
					"mrt-row-actions": {
						Header: "", //change header text
						size: 100, //make actions column smaller
					},
				}}
				positionToolbarAlertBanner="top"
				muiTableBodyProps={{
					sx: () => ({
						"& tr:nth-of-type(odd)": {
							backgroundColor: "#8578741A",
						},
					})
				}}
				muiToolbarAlertBannerProps={
					!!dateError
						? {
							color: 'error',
							children: dateError,
						}
						: !!previewFlag ? uploadError ? {
							color: 'error',
							children: "Please download the spreadsheet with errors column and fix the errors in the error column before submitting",
						} : {
							color: 'warning',
							children: "There are no errors in the uploaded spreadsheet. You can submit the pipeline now",
						} : undefined

				}
				muiTableBodyRowProps={(table) => {
					return {
						sx: () => ({
							// @ts-ignore
							"textDecoration": previewFlag ? previewData[table.row.index]?.RemoveProjectFromModel ? "line-through" : "none" : projectList?.executeGetScenarioProjectList[table.row.index]?.RemoveProjectFromModel ? "line-through" : "none",
						})
					}
				}}
				positionActionsColumn="first"
				renderRowActionMenuItems={({ closeMenu, row, table }) => [
					<MenuItem
						key={0}
						onClick={() => {
							table.setEditingRow(row);
							closeMenu();
						}}
						sx={{ m: 0 }}
					>
						<ListItemIcon>
							<EditIcon />
						</ListItemIcon>
						Edit
					</MenuItem>,
					<MenuItem
						key={1}
						onClick={() => {
							handleDeleteProject(row);
							closeMenu();
						}}
						sx={{ m: 0 }}
						disabled={!!row.original.RemoveProjectFromModel}
					>
						<ListItemIcon>
							<DeleteForeverRoundedIcon />
						</ListItemIcon>
						Delete
					</MenuItem>,
					<MenuItem
						key={2}
						onClick={() => {
							handleRestoreProject(row);
							closeMenu();
						}}
						sx={{ m: 0 }}
						disabled={!row.original.RemoveProjectFromModel}
					>
						<ListItemIcon>
							<ReplayRoundedIcon />
						</ListItemIcon>
						Restore
					</MenuItem>,
				]}
				renderBottomToolbarCustomActions={({ }) => {

					const handleGenerateScenario = () => {
						setTabIndex(3);
						runModel();
					};

					return (
						<Box
							sx={{ display: "flex", gap: "0.5rem", p: 2, justifyContent: "flex-end", width: "100%" }}
						>
							<Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, justifyContent: "flex-end", fontFamily: "ProximaNova-Bold", marginRight: "auto" }}>
								<strong>{caveatIntro}</strong>
							</Box>
							<Button
								onClick={() => handleUpdateTab(0)}
								variant="contained"
								startIcon={<ArrowBackIcon />}
							>
								Back
							</Button>
							<Button variant='contained' onClick={handleConfirmSubmit} disabled={isFetching || uploadError}>Submit</Button>
							<Button
								onClick={handleGenerateScenario}
								variant="contained"
								disabled={previewFlag}
							>
								Generate scenario
							</Button>

						</Box>
					);

				}}
				renderToolbarInternalActions={({ table }) => (
					// render an empty action are to disable the duplicated actions(full screen etc.)
					<Box>
						<MRT_ShowHideColumnsButton table={table} />
						<MRT_ToggleFiltersButton table={table} />
						<MRT_FullScreenToggleButton table={table} />
						<Tooltip arrow title={"Export All Data"}>
							<label><Button
								variant="outlined"
								disabled={downloading || isFetching}
								onClick={() => {
									setDownloading(true);
								}}
								endIcon={<FileDownloadIcon />}
							>DOWNLOAD</Button></label>
						</Tooltip>
						<input ref={fileInputRef} id='file-input' type='file' style={{ display: "none" }} onChange={handleFileChange}
							accept=".xls,.xlsx,.csv" />
						<Tooltip arrow title={"Import All Data"}>
							<label htmlFor="file-input">
								<Button sx={{ ml: 1 }}
									variant='outlined' component='span' disabled={isFetching} endIcon={<FileUpLoadIcon />}>UPLOAD
								</Button>
							</label>
						</Tooltip>
					</Box>
				)}
			/>
			<CreateNewAccountModal
				// @ts-ignore
				columns={columns}
				open={createModalOpen}
				onClose={() => setCreateModalOpen(false)}
				onSubmit={handleCreateNewRowModal}
				AllLists={AllLists}
				projectList={projectList}
        // @ts-ignore
				Juri={userInfo?.executeGetUser[0].Jurisdiction as string ?? ""}
			/>
			<Dialog open={uploadScenarioProjects.isLoading} aria-describedby='upload-progress' maxWidth="xs">
				<DialogTitle>The file is uploading...</DialogTitle>
				<DialogContent>
					<LinearProgress id='upload-progress' />
				</DialogContent>
			</Dialog>
		</Box>
	);
}
